/**
 * Popup utilities
 *
 * A popup is a component that will always be on top of the page.
 * By default it will use the dialog element, but it can also be a div for retrocompatibility reasons.
 * The dialog element should be preferred.
 */

import { submitElement } from "../confirms.js";
import { getEl } from "../utils.js";
import * as UI from "../ui.js";
import * as Component from "../component";

let popupStack = [];

/**
 * The popup:open event is not a DOM event.
 * It's usually triggered by a button with the data-action="popup:open" and data-target attribute set.
 */
UI.onDataActionClick("popup:open", ({ el, target }) => {
  const popup = target ?? el.closest(".popup");
  open(popup);
});

/**
 * The popup:close event is not a DOM event.
 * It's usually triggered by a button with the data-action="popup:close" inside a popup.
 * It makes it easy to close a popup however it's not the same thing as the native close event.
 * DO NOT LISTEN TO THIS EVENT TO KNOW IF A POPUP CLOSED.
 * Instead listen to the native close event which is emulated when needed.
 */
UI.onDataActionClick("popup:close", ({ el, target }) => {
  const popup = target ?? el.closest(".popup");
  close(popup);
});

UI.onDataActionClick("popup:confirm", async ({ el, target, event }) => {
  event.preventDefault();
  event.stopPropagation();

  const popup = target ?? el.closest(".popup");
  if (await confirm(popup)) {
    submitElement(el);
  }
});

/**
 * Open a popup.
 * @param {HTMLDialogElement|HTMLDivElement} el The popup element
 */
export function open(el) {
  UI.toggleNoScroll(true);
  popupStack.push(el);

  // See the documentation in the Elixir component for more information about
  // the `legacy` attribute
  if (!(el instanceof HTMLDialogElement)) {
    el.classList.add("popup__non-dialog--open");
    const handleKeyDown = function (event) {
      // Event target is the body
      const isCKEditorDialogOpen = event.target.classList.contains("cke_dialog_open");
      if (event.key === "Escape" && popupStack[popupStack.length - 1] === el && !isCKEditorDialogOpen) {
        close(el);
        document.removeEventListener("keydown", handleKeyDown);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
  } else {
    el.showModal();
  }

  const controller = new AbortController();

  el.addEventListener(
    "close",
    () => {
      popupStack = popupStack.filter((popup) => popup !== el);
      if (popupStack.length === 0) {
        UI.toggleNoScroll(false);
      }
      controller.abort();
    },
    { once: true }
  );

  if ("clickOutsideToDismiss" in el.dataset) {
    // Listen for clicks outside the popup
    // Dialog element takes full width and height on the page
    // We assume clicking on the dialog element is clicking on the backdrop since it has no margin / padding
    // Signal is used to remove the event listener when popup is closed
    el.addEventListener(
      "click",
      (event) => {
        if (event.target == el) {
          close(el);
        }
      },
      {
        signal: controller.signal,
      }
    );
  }
}

/**
 * Drop a popup from the stack without emitting a close event.
 *
 * For popups torn out of the DOM rather than closed — a LiveView re-render swapping one popup for
 * another, say. They never fire `close`, so without this they stay on the stack and keep the scroll
 * locked for good.
 *
 * @param {HTMLDialogElement|HTMLDivElement} el The popup element
 */
export function forget(el) {
  popupStack = popupStack.filter((popup) => popup !== el);
  if (popupStack.length === 0) UI.toggleNoScroll(false);
}

/**
 * Close a popup.
 * @param {HTMLDialogElement|HTMLDivElement} el The popup element
 */
export function close(el) {
  if (!(el instanceof HTMLDialogElement)) {
    el.classList.remove("popup__non-dialog--open");
    // Emulates the native close event
    // This way we can listen to the close event on both dialogs and non-dialog popups regardless of the way they were closed
    const customCloseEvent = new Event("close");
    el.dispatchEvent(customCloseEvent);
    return;
  }

  el.close();
}

/**
 * Wait for a popup to close and return its returnValue.
 *
 * The returnValue is set either automatically with the value of
 * the <button> submitting a <form method="dialog"> within the dialog,
 * or manually by calling `dialogEl.close(<returnValue>)`.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog#handling_the_return_value_from_the_dialog
 * for more information about returnValue.
 *
 * @param {HTMLDialogElement|HTMLDivElement} el The popup element
 * @returns {Promise<any>}
 */
export async function wait(el) {
  return new Promise((resolve) => {
    el.addEventListener("close", (event) => resolve(event.target.returnValue), { once: true });
  });
}

/**
 * Open a popup and return wait for it to close. When it closes, the promise
 * resolves to the dialog returnValue.
 *
 * @see open
 * @see wait
 *
 * @param {HTMLDialogElement|HTMLDivElement} el The popup element
 * @returns {Promise<any>}
 */
export async function openAndWait(el) {
  open(el);
  return wait(el);
}

/**
 * A wrapper around `openAndWait` that resolves to a boolean,
 * corresponding to the user's choice to confirm or cancel the dialog.
 *
 * @see openAndWait
 *
 * @param {HTMLDialogElement|HTMLDivElement} elOrSelector The popup element or a selector
 * @returns {Promise<boolean>}
 */
export async function confirm(elOrSelector) {
  const el = getEl(elOrSelector);
  const result = await openAndWait(el);
  return result === "confirm";
}

/**
 * Creates a popup on the fly.
 *
 * @param {HTMLElement} contentEl Appended to the inner of the popup
 * @param {*} containerEl Future parent of the popup
 * @param {*} extraClassName Added to the popupEl
 * @returns {HTMLElement}
 */
export function create(contentEl, containerEl, extraClassName = null) {
  const popupInner = document.createElement("div");
  popupInner.classList.add("popup__inner");
  popupInner.appendChild(contentEl);

  const popupEl = document.createElement("dialog");
  popupEl.classList.add("popup", extraClassName);
  popupEl.appendChild(popupInner);

  containerEl.appendChild(popupEl);

  open(popupEl);

  Component.setExtra(contentEl, "containerEl", containerEl);
  Component.setExtra(contentEl, "popupEl", popupEl);

  return popupEl;
}

/**
 * Destroy a popup created on the fly.
 * Retrieves the popup element from the el added to the popup.
 *
 * @param {HTMLElement} contentEl Element originally added to the popup
 */
export function destroy(contentEl) {
  const popupEl = Component.popExtra(contentEl, "popupEl");
  close(popupEl);
  popupEl.remove();
}

export function initForAlpine(Alpine) {
  Alpine.magic("popup", () => ({
    open,
    close,
    wait,
    openAndWait,
    confirm,
  }));
}

/**
 * Open any popup that receives a `popup:open` DOM event.
 * Examples:
 *   - LiveView-dynamic popup that should open when LV adds it to the DOM:
 *     `<.popup phx-mounted={JS.dispatch("popup:open")}>`
 *   - Open another popup from an arbitrary event:
 *     `phx-click={JS.dispatch("popup:open", to: "#target-popup")}`
 *   DOMContentLoaded only fires once on initial load, so LV-inserted popups need this hook.
 */
document.addEventListener("popup:open", (event) => {
  open(event.target);
});

document.addEventListener("DOMContentLoaded", () => {
  const openables = document.querySelectorAll("*[data-open-popup-on-render]");
  openables.forEach((el) => {
    open(el);
  });

  // Popups with data-set-cookie-on-close write a timestamp cookie when closed (by any means).
  const cookiePopups = document.querySelectorAll("[data-set-cookie-on-close]");
  cookiePopups.forEach((el) => {
    el.addEventListener(
      "close",
      () => {
        const name = el.dataset.setCookieOnClose;
        document.cookie = `${name}=${new Date().toISOString()};max-age=31536000;path=/`;
      },
      { once: true }
    );
  });
});
